import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Button, Card, CardContent, CardMedia, CircularProgress, Grid, Tooltip, Typography, useTheme } from "@mui/material";
import ImageIcon from "@mui/icons-material/Image";
import SwapHorizRoundedIcon from "@mui/icons-material/SwapHorizRounded";
import { AlternativeSiteDetail, Compound, OfferCompoundService, OfferSiteService, StayDetail } from "../../services/restserver-openapi";
import SingleSiteProposalCard from "./SingleSiteProposalCard";
import { getTourDetailWithAlternativeRadius } from "../../utils/Vanlifer/tour";
import { useAppDispatch, useAppSelector } from "../../hooks/hooks";
import { getReadableCompoundType } from "../../utils/typeUtils";
import { fetchEditedTourDetails } from "../../store/Vanlifer/tourSlice";

const FIXED_ALTERNATIVE_RADIUS_KM = 10;

type Props = {
    stay: StayDetail;

    // Layout
    lineColor: string;
    thickness?: number;

    // Controlled Accordion
    expanded: boolean;
    onToggle: () => void;

    // Editing
    editingEnabled: boolean;
    onToggleEditMode: () => void;
    canActivateEditMode: boolean;
    showEditAction: boolean;
    editDisabledTooltip: string;
    isDirty: boolean;

    // Controlled calendar visibility
    showStayCalendar: boolean;
    onHoverChange?: (hovered: boolean) => void;
    hovered?: boolean;
};

export default function StayProposalTimelineItem({
                                                     stay,
                                                     lineColor,
                                                     thickness = 3,
                                                      expanded,
                                                      onToggle,
                                                      editingEnabled,
                                                       onToggleEditMode,
                                                       canActivateEditMode,
                                                       showEditAction,
                                                        editDisabledTooltip,
                                                        isDirty,
                                                     showStayCalendar,
                                                     onHoverChange,
                                                     hovered = false,
                                                     }: Props) {
    const theme = useTheme();
    const dispatch = useAppDispatch();
    const selectedTourId = useAppSelector((state) => state.tour.choosedTourDetail.tourId);
    const selectedTourStatus = useAppSelector((state) => state.tour.choosedTourDetail.status);
    const [loadingAlternatives, setLoadingAlternatives] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [loadedAlternatives, setLoadedAlternatives] = useState<Array<AlternativeSiteDetail> | null>(null);
    const [alternativeCompoundsBySiteId, setAlternativeCompoundsBySiteId] = useState<Record<number, Compound>>({});
    const [showAlternatives, setShowAlternatives] = useState(false);
    const [switchingSiteId, setSwitchingSiteId] = useState<number | null>(null);
    const effectiveTourId = stay.tourId ?? selectedTourId;

    const shownAlternatives = useMemo(
        () => loadedAlternatives ?? stay.alternativeSites ?? [],
        [loadedAlternatives, stay.alternativeSites]
    );

    const alternativesWithoutCurrent = useMemo(() => {
        const currentSiteId = stay.staySite?.siteId;
        return shownAlternatives.filter((alternative) => alternative.siteId != null && alternative.siteId !== currentSiteId);
    }, [shownAlternatives, stay.staySite?.siteId]);

    const shownTopAlternatives = useMemo(() => {
        const origin = stay.stayCompound?.coordinates;

        const distance = (alternative: AlternativeSiteDetail) => {
            if (
                origin?.x == null ||
                origin?.y == null ||
                alternative.coordinates?.x == null ||
                alternative.coordinates?.y == null
            ) {
                return Number.MAX_VALUE;
            }

            return Math.hypot(origin.x - alternative.coordinates.x, origin.y - alternative.coordinates.y);
        };

        return [...alternativesWithoutCurrent]
            .sort((a, b) => distance(a) - distance(b))
            .slice(0, 3);
    }, [alternativesWithoutCurrent, stay.stayCompound?.coordinates]);

    useEffect(() => {
        let isCancelled = false;

        const loadAlternativeCompounds = async () => {
            if (shownTopAlternatives.length === 0) {
                setAlternativeCompoundsBySiteId({});
                return;
            }

            const entries = await Promise.all(
                shownTopAlternatives.map(async (alternative) => {
                    if (!alternative.siteId) {
                        return null;
                    }

                    try {
                        const site = await OfferSiteService.getSite(alternative.siteId);
                        if (!site.compoundId) {
                            return null;
                        }

                        const compound = await OfferCompoundService.getCompound(site.compoundId);
                        return [alternative.siteId, compound] as const;
                    } catch (metadataError) {
                        console.error("Unable to load alternative compound metadata", metadataError);
                        return null;
                    }
                })
            );

            if (isCancelled) {
                return;
            }

            const mappedCompounds: Record<number, Compound> = {};
            entries.forEach((entry) => {
                if (entry) {
                    mappedCompounds[entry[0]] = entry[1];
                }
            });
            setAlternativeCompoundsBySiteId(mappedCompounds);
        };

        loadAlternativeCompounds();

        return () => {
            isCancelled = true;
        };
    }, [shownTopAlternatives]);

    const handleLoadAlternatives = useCallback(async () => {
        if (effectiveTourId == null) {
            setError("Tour konnte nicht ermittelt werden.");
            return;
        }

        setLoadingAlternatives(true);
        setError(null);
        setShowAlternatives(true);
        try {
            const tourDetail = await getTourDetailWithAlternativeRadius(effectiveTourId, FIXED_ALTERNATIVE_RADIUS_KM);
            const matchingStay = (tourDetail.stays ?? []).find((item) => {
                if (stay.stayId && item.stayId) {
                    return item.stayId === stay.stayId;
                }

                return item.staySite?.siteId === stay.staySite?.siteId && item.fromDate === stay.fromDate && item.toDate === stay.toDate;
            });
            setLoadedAlternatives(matchingStay?.alternativeSites ?? []);
        } catch (requestError) {
            console.error("Loading alternatives failed", requestError);
            setError("Alternativen konnten nicht geladen werden.");
        } finally {
            setLoadingAlternatives(false);
        }
    }, [effectiveTourId, stay.stayId, stay.staySite?.siteId, stay.fromDate, stay.toDate]);

    const handleSwitchSite = async (newSiteId?: number) => {
        if (!stay.stayId || !effectiveTourId || !newSiteId) {
            return;
        }

        if (selectedTourStatus !== "RESERVED" && selectedTourStatus !== "BOOKED") {
            setError("Stellplatzwechsel ist nur bei reservierten oder gespeicherten Touren möglich.");
            return;
        }

        setSwitchingSiteId(newSiteId);
        setError(null);
        try {
            await dispatch(
                fetchEditedTourDetails({
                    tourId: effectiveTourId,
                    stayId: stay.stayId,
                    chosenSiteId: newSiteId,
                })
            ).unwrap();
            setLoadedAlternatives(null);
        } catch (switchError) {
            console.error("Switching stay site failed", switchError);
            const maybeApiError = switchError as { body?: { message?: string } };
            setError(maybeApiError?.body?.message ?? "Stellplatz konnte nicht gewechselt werden.");
        } finally {
            setSwitchingSiteId(null);
        }
    };

    useEffect(() => {
        if (editingEnabled) {
            setShowAlternatives(true);
            handleLoadAlternatives();
            return;
        }

        if (!editingEnabled) {
            setShowAlternatives(false);
            setLoadedAlternatives(null);
            setAlternativeCompoundsBySiteId({});
            setError(null);
        }
    }, [editingEnabled, handleLoadAlternatives]);

    return (
        <Box sx={{ display: "flex", mb: 3 }}>
            {/* Left timeline line */}
            <Box
                sx={{
                    borderLeft: `${thickness}px solid ${lineColor}`,
                    borderRadius: 2,
                    mr: 2,
                    width: `${thickness}px`,
                }}
            />
            <Box sx={{ flex: 1 }}>
                <SingleSiteProposalCard
                    stay={stay}
                    canEdit={editingEnabled}
                    canActivateEditMode={canActivateEditMode}
                    showEditAction={showEditAction}
                    editDisabledTooltip={editDisabledTooltip}
                    onToggleEditMode={onToggleEditMode}
                    showStayCalendar={showStayCalendar}
                    expanded={expanded}
                    onToggle={onToggle}
                    isDirty={isDirty}
                    onHoverChange={onHoverChange}
                    hovered={hovered}
                    alternativesPanel={editingEnabled && showAlternatives ? (
                        <Box sx={{ mt: 0.5 }}>
                            {loadingAlternatives && <CircularProgress size={18} />}

                            {error && (
                                <Typography variant="caption" color="error" sx={{ display: "block", mt: 0.8 }}>
                                    {error}
                                </Typography>
                            )}

                            {!loadingAlternatives && !error && shownTopAlternatives.length === 0 && (
                                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.8 }}>
                                    Keine Alternativen in der Nähe gefunden.
                                </Typography>
                            )}

                            <Grid container spacing={1} sx={{ mt: 0.2 }}>
                                {shownTopAlternatives.map((alternative, index) => {
                                    const compound = alternative.siteId ? alternativeCompoundsBySiteId[alternative.siteId] : undefined;
                                    const imageId = compound?.imageIds?.[0];

                                    return (
                                        <Grid item xs={12} sm={6} md={4} key={`${alternative.siteId ?? "unknown"}-${index}`}>
                                        <Box sx={{ display: "block" }}>
                                            <Card
                                                sx={{
                                                    boxShadow: 2,
                                                        backgroundColor: "background.paper",
                                                        border: `1px solid ${theme.palette.divider}`,
                                                        height: "100%",
                                                    }}
                                                >
                                                    {imageId ? (
                                                        <CardMedia
                                                            component="img"
                                                            height={140}
                                                            image={`${process.env.REACT_APP_API_URL}/image/${imageId}`}
                                                            alt={compound?.name ?? alternative.compoundName ?? "Alternative"}
                                                            sx={{ objectFit: "cover" }}
                                                        />
                                                    ) : (
                                                        <Box
                                                            sx={{
                                                                height: 140,
                                                                display: "flex",
                                                                alignItems: "center",
                                                                justifyContent: "center",
                                                                bgcolor: "grey.100",
                                                            }}
                                                        >
                                                            <ImageIcon color="disabled" />
                                                        </Box>
                                                    )}
                                                    <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
                                                        <Typography sx={{ textTransform: "uppercase" }} fontSize={11} gutterBottom>
                                                            {compound ? getReadableCompoundType(compound) : "Alternative"}
                                                        </Typography>
                                                        <Typography variant="body2" fontWeight={700} gutterBottom>
                                                            {compound?.name ?? alternative.compoundName ?? `Site ${alternative.siteId ?? "-"}`}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                                                            Site-ID: {alternative.siteId ?? "-"}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                                                            Preisdiff: {alternative.priceDifference ?? 0} CHF
                                                        </Typography>
                                                    {alternative.rating != null && (
                                                        <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                                                            Rating: {alternative.rating}
                                                        </Typography>
                                                    )}

                                                    <Box sx={{ display: "flex", justifyContent: "center", mt: 1 }}>
                                                        <Tooltip title={selectedTourStatus === "RESERVED" || selectedTourStatus === "BOOKED" ? "" : "Nur bei reservierter oder gespeicherter Tour möglich"}>
                                                            <span>
                                                                <Button
                                                                    size="small"
                                                                    variant="contained"
                                                                    startIcon={<SwapHorizRoundedIcon />}
                                                                    onClick={() => handleSwitchSite(alternative.siteId)}
                                                                    disabled={switchingSiteId === alternative.siteId || (selectedTourStatus !== "RESERVED" && selectedTourStatus !== "BOOKED")}
                                                                >
                                                                    Wechseln
                                                                </Button>
                                                            </span>
                                                        </Tooltip>
                                                    </Box>
                                                </CardContent>
                                            </Card>
                                        </Box>
                                        </Grid>
                                    );
                                })}
                            </Grid>
                        </Box>
                    ) : undefined}
                />
            </Box>
        </Box>
    );
}
